/**
 * Хуки загрузки данных.
 *
 * Своё вместо готовой библиотеки: экранов пять, запросы простые, а тянуть
 * ради них клиент кеширования — это плюс сотня килобайт на связи в зале.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

export interface AsyncState<T> {
  data: T | null
  error: unknown
  loading: boolean
  /** Перезапросить. Возвращает промис — удобно ждать после действия. */
  reload: () => Promise<void>
  /**
   * Подменить данные локально, не дожидаясь сервера.
   *
   * Принимает и значение, и функцию-обновление. Функция здесь не роскошь:
   * при «показать ещё» страница летит с сервера, и если за это время удалить
   * строку, то `set([...data, ...page])` с захваченным старым `data` воскресит
   * удалённую запись. Функциональная форма читает актуальное состояние.
   */
  set: Dispatch<SetStateAction<T | null>>
}

/**
 * Загрузить данные при монтировании и по изменению зависимостей.
 *
 * Ответ устаревшего запроса отбрасывается: пользователь может переключить
 * упражнение быстрее, чем ответит сервер, и без этой проверки на экране
 * оказались бы чужие цифры.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const generation = useRef(0)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const run = useCallback(async () => {
    const mine = ++generation.current
    setLoading(true)
    setError(null)
    try {
      const result = await fn()
      if (alive.current && mine === generation.current) setData(result)
    } catch (e) {
      if (alive.current && mine === generation.current) setError(e)
    } finally {
      if (alive.current && mine === generation.current) setLoading(false)
    }
    // fn пересоздаётся на каждый рендер, поэтому в зависимостях его нет —
    // управляем перезапросом через deps, переданные вызывающим.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    void run()
  }, [run])

  return { data, error, loading, reload: run, set: setData }
}

/** Значение, которое меняется не чаще, чем раз в `ms`. */
export function useThrottled<T>(value: T, ms = 300): T {
  const [throttled, setThrottled] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setThrottled(value), ms)
    return () => window.clearTimeout(timer)
  }, [value, ms])
  return throttled
}
